"use client";

/**
 * HtmlViewerToolbar — compact actions for a rendered HTML file.
 *
 * The document stays read-only: users can open the dedicated pop-out viewer
 * or download the exact raw HTML fetched for the iframe.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface HtmlViewerToolbarProps {
  /** Raw HTML content to save as a local file. */
  content: string | null;
  /** Display filename, also used for the downloaded filename. */
  fileName: string;
  /** Signed file URL — required for the pop-out action. */
  fileUrl?: string;
  /** Original workbook URL for a rendered XLSX preview. */
  sourceUrl?: string;
  /** Called when the user wants to open the pop-out HTML viewer. */
  onOpenInNewWindow?: () => void;
  className?: string;
}

function htmlDownloadName(fileName: string): string {
  const baseName = fileName.trim() || "document";
  return `${baseName.replace(/\.html?$/i, "")}.html`;
}

export function HtmlViewerToolbar({
  content,
  fileName,
  fileUrl,
  sourceUrl,
  onOpenInNewWindow,
  className,
}: HtmlViewerToolbarProps) {
  const { t } = useTranslation();

  const handleDownload = useCallback(() => {
    if (content == null) return;

    const blob = new Blob([content], { type: "text/html; charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = htmlDownloadName(fileName);
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);
  }, [content, fileName]);

  return (
    <div className={cn("flex items-center gap-1", className)} data-testid="html-viewer-toolbar">
      {fileUrl && onOpenInNewWindow && (
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          onClick={onOpenInNewWindow}
          aria-label={t("viewer.openInNewWindow")}
          title={t("viewer.openInNewWindow")}
        >
          <ExternalLink className="size-4" />
        </Button>
      )}
      {sourceUrl ? <Button asChild variant="ghost" size="icon" aria-label={t("viewer.downloadContent")} title={t("viewer.downloadContent")}>
        <a href={sourceUrl} download={fileName}><Download className="size-4" /></a>
      </Button> : <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        disabled={content == null}
        onClick={handleDownload}
        aria-label={t("viewer.downloadHtml")}
        title={t("viewer.downloadHtml")}
        data-testid="html-viewer-download-button"
      >
        <Download className="size-4" />
      </Button>}
    </div>
  );
}
